// src/pages/ProjectDetailPage.jsx
import React from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Separator from "@radix-ui/react-separator";
import TagPill from "../components/dossier/TagPill";
import SessionAccordion from "../components/dossier/SessionAccordion";
import { buildDossierPath, DOSSIER_MODES, isExpressDossierMode } from "../data/dossierModes";
import DossierEmptyState from "../components/dossier/DossierEmptyState";

/* ------------------------------ Utils ------------------------------ */
const removeVS = (s = "") => String(s).replace(/\uFE0F/g, "");
const cleanLine = (s = "") =>
  removeVS(s)
    .replace(/^\s*(?:[•●▪▫○◦·*]|[-–—])\s*/u, "")
    .trim();
const toArray = (val) => {
  if (Array.isArray(val)) return val.map(cleanLine).filter(Boolean);
  if (typeof val === "string")
    return val
      .replace(/\r/g, "")
      .split("\n")
      .map(cleanLine)
      .filter(Boolean);
  return [];
};
const parsePrepareSection = (section) => {
  if (section && typeof section === "object" && !Array.isArray(section)) {
    const items = toArray(section.passos || section.items || []);
    const role = typeof section.rol_docent === "string" ? section.rol_docent : "";
    return { items, role };
  }
  if (typeof section === "string") {
    const idx = section.indexOf("🎓");
    if (idx >= 0) {
      const before = section.slice(0, idx);
      const after = section.slice(idx);
      const items = toArray(before);
      const role = after
        .replace(/^🎓\s*Rol del docent:\s*/i, "")
        .replace(/\n\s*/g, " ")
        .trim();
      return { items, role };
    }
    return { items: toArray(section), role: "" };
  }
  return { items: [], role: "" };
};

/** Grupo de pills (continguts/competències) */
const PillGroup = ({ items, seed = "session" }) => {
  const list = toArray(items);
  if (!list.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {list.map((label, i) => (
        <TagPill key={`${label}-${i}`} label={label} seed={seed} index={i} />
      ))}
    </div>
  );
};


const Card = ({ title, children, className = "" }) => (
  <section className={`bg-white/85 backdrop-blur rounded-2xl border p-5 md:p-6 space-y-3 ${className}`}>
    {title && <h3 className="text-lg md:text-xl font-semibold">{title}</h3>}
    {children}
  </section>
);
const BulletList = ({ items, marker = "•", markerClass = "text-gray-400" }) => {
  const list = toArray(items);
  if (!list.length) return null;
  return (
    <ul className="space-y-1.5 leading-relaxed">
      {list.map((it, i) => (
        <li key={i} className="flex gap-2">
          <span className={`select-none mt-[3px] inline-block min-w-[1em] text-center ${markerClass}`}>{marker}</span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
};

/* --------------------------------- Página --------------------------------- */
export default function ProjectDetailPage({ mode = DOSSIER_MODES.STANDARD }) {
  const { phaseKey } = useParams();
  const { t } = useTranslation();

  if (isExpressDossierMode(mode)) {
    return <DossierEmptyState />;
  }

  const base = `dossier_didactic.projectes.${phaseKey}`;

  // Cabecera
  const guia = t(`${base}.guia`, { defaultValue: "" });
  const titol = t(`${base}.titol_apartat`, { defaultValue: "" });
  const descripcio = t(`${base}.descripcio`, { defaultValue: "" });

  // Blocs + competències
  const continguts = t(`${base}.blocs_i_competencies.continguts`, { returnObjects: true, defaultValue: [] });
  const competencies = t(`${base}.blocs_i_competencies.competencies`, { returnObjects: true, defaultValue: [] });

  // Sidebar
  const material = t(`${base}.material`, { returnObjects: true, defaultValue: [] });
  const recursos = t(`${base}.recursos`, { returnObjects: true, defaultValue: [] });

  // Preparem
  const rawEng = t(`${base}.preparem.enganxem`, { returnObjects: true, defaultValue: "" });
  const rawExp = t(`${base}.preparem.explorem`, { returnObjects: true, defaultValue: "" });
  const rawExi = t(`${base}.preparem.expliquem`, { returnObjects: true, defaultValue: "" });
  const rawEla = t(`${base}.preparem.elaborem`, { returnObjects: true, defaultValue: "" });
  const rawAva = t(`${base}.preparem.avaluem`, { returnObjects: true, defaultValue: "" });
  const eng = parsePrepareSection(rawEng);
  const exp = parsePrepareSection(rawExp);
  const exi = parsePrepareSection(rawExi);
  const ela = parsePrepareSection(rawEla);
  const ava = parsePrepareSection(rawAva);

  // Criteris
  const criteris = t(`${base}.criteris_avaluacio`, { returnObjects: true, defaultValue: [] });

  const notFound = !guia && !titol && !descripcio;
  if (notFound) {
    return (
      <div className="rounded-2xl border bg-white p-6">
        <p className="text-red-600">
          {t("ui.dossier.errors.projectPhaseNotFound", "Fase del projecte no trobada.")}
        </p>
        <Link to={buildDossierPath("/dossier/projecte", mode)} className="underline mt-2 inline-block">
          {t("ui.dossier.links.backProject", "← Tornar al projecte")}
        </Link>
      </div>
    );
  }

  const hasPrepare = [eng, exp, exi, ela, ava].some((s) => s.items?.length || s.role);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-1">
        {!!guia && (
          <h2
            className="font-extrabold tracking-tight"
            style={{ fontSize: "clamp(28px, 4.5vw, 40px)", lineHeight: 1.08 }}
          >
            {guia}
          </h2>
        )}
        {!!titol && <p className="text-sm text-gray-700">{titol}</p>}
      </div>

      {/* Descripció + Materials */}
      <div className="grid lg:grid-cols-12 gap-4">
        {!!descripcio && (
          <Card className="lg:col-span-8">
            <div className="space-y-3 text-[15px] leading-[1.75]">
              <p className="whitespace-pre-line">{descripcio}</p>
            </div>
          </Card>
        )}

        {!!toArray(material).length && (
          <Card className={`lg:col-span-4 ${!descripcio ? "lg:col-start-1" : ""}`} title={t("ui.common.materials", "Materials")}>
            <BulletList items={material} marker="▸" markerClass="text-emerald-600" />
            {!!toArray(recursos).length && (
              <>
                <Separator.Root className="bg-gray-200 h-px w-full my-3" />
                <h4 className="font-semibold mb-2">{t("ui.common.resources", "Recursos")}</h4>
                <BulletList items={recursos} marker="▹" markerClass="text-gray-500" />
              </>
            )}
          </Card>
        )}
      </div>

      {/* Continguts & Competències */}
      {(toArray(continguts).length || toArray(competencies).length) && (
        <Card title={t("ui.common.content_and_competencies", "Blocs de contingut i competències")}>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold mb-2">{t("ui.common.contents", "Continguts")}</h4>
              <PillGroup items={continguts} />
            </div>
            <div>
              <h4 className="font-semibold mb-2">{t("ui.common.competencies", "Competències")}</h4>
              <PillGroup items={competencies} />
            </div>
          </div>
        </Card>
      )}

      <Separator.Root className="bg-gray-200 h-px w-full" />

      {/* Preparem */}
      {hasPrepare && (
        <div className="space-y-3">
          <h3 className="text-lg md:text-xl font-semibold">{t("ui.common.prepare", "Preparem")}</h3>
          <SessionAccordion
            labels={{
              hook: t("ui.prepare.blocks.enganxem", "Enganxem"),
              explore: t("ui.prepare.blocks.explorem", "Explorem"),
              explain: t("ui.prepare.blocks.expliquem", "Expliquem"),
              elaborate: t("ui.prepare.blocks.elaborem", "Elaborem"),
              assess: t("ui.prepare.blocks.avaluem", "Avaluem"),
            }}
            prepare={{
              enganxem: { items: eng.items, role: eng.role },
              explorem: { items: exp.items, role: exp.role },
              expliquem: { items: exi.items, role: exi.role },
              elaborem: { items: ela.items, role: ela.role },
              avaluem: { items: ava.items, role: ava.role },
            }}
          />
        </div>
      )}

      {/* Criteris */}
      {!!toArray(criteris).length && (
        <Card title={t("ui.common.evaluation", "Criteris d’avaluació")}>
          <BulletList items={criteris} />
        </Card>
      )}

      {/* Volver */}
      <div className="pt-1">
        <Link to={buildDossierPath("/dossier/projecte", mode)} className="underline">
          {t("ui.dossier.links.backProject", "← Tornar al projecte")}
        </Link>
      </div>
    </div>
  );
}
